import { ARENA_RADIUS, TICK_RATE } from './constants.ts';
import { ITEMS, SHOP } from './items.ts';
import type { Simulation } from './simulation.ts';
import type { MatchConfig, MatchSetup } from './match.ts';
import { eventVisibleTo, isVisible, VISION_NET_PADDING, visionSources } from './vision.ts';
import {
  emptyInput,
  type AbilitySlot,
  type ActionKind,
  type Cooldowns,
  type HeroId,
  type HeroState,
  type InputState,
  type MatchState,
  type MinionKind,
  type MinionState,
  type ProjectileKind,
  type ProjectileState,
  type SimEvent,
  type StructureKind,
  type StructureState,
  type Team,
} from './types.ts';

export const NET_PROTOCOL_VERSION = 18;
export const NET_ROOM_NAME = 'brawl';
export const NET_INPUT_INTERVAL_MS = 1000 / 30;
export const NET_INPUT_TIMEOUT_MS = 500;
/** El cliente muestra el mundo ~100 ms por detrás del último snapshot para interpolar. */
export const NET_INTERP_TICKS = 3;
/** Con la red estable el margen baja hasta ~66 ms (`SnapshotPlayback` adaptativo). */
export const NET_INTERP_MIN_TICKS = 2;

/** Redondeo para el cable: 2 decimales bastan y achican mucho el JSON. */
const q = (value: number): number => Math.round(value * 100) / 100;

export interface NetInputMessage {
  seq: number;
  input: InputState;
}

export interface WelcomeMessage {
  protocol: number;
  sessionId: string;
  roomId: string;
  heroId: number;
  heroIndex: number;
  tickRate: number;
  /** configuración y alineación: el cliente crea la misma simulación (`createMatchSimulation`) */
  setup: MatchSetup;
}

/** Opciones al crear/unirse a una sala. */
export interface NetJoinOptions {
  protocol: number;
  name: string;
  heroId: HeroId;
  /** sesión de la cuenta (opcional): el servidor usa el nombre de la cuenta */
  token?: string;
}

export interface LobbyPlayer {
  sessionId: string;
  name: string;
  team: Team;
  heroId: HeroId;
  host: boolean;
  /** jugador con cuenta (los invitados no la tienen) */
  account?: boolean;
}

/**
 * Estado de la sala (mensaje `lobby`). En la fase `lobby` el anfitrión elige el
 * modo y cada jugador su héroe y equipo; `playing` = partida en curso (quien
 * entra entonces ocupa el puesto de un bot).
 */
export interface LobbyState {
  protocol: number;
  roomId: string;
  phase: 'lobby' | 'playing';
  hostId: string;
  config: MatchConfig;
  players: LobbyPlayer[];
}

/** Mensajes del lobby (cliente → servidor). */
export type LobbyCommand =
  | { type: 'config'; config: MatchConfig }
  | { type: 'hero'; heroId: HeroId }
  | { type: 'team'; team: Team }
  | { type: 'start' };

/** Nombre de jugador saneado (vacío si no vale). */
export function sanitizePlayerName(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const clean = Array.from(raw).filter((c) => c >= ' ' && c !== '<' && c !== '>').join('');
  return clean.trim().slice(0, 16);
}

/** Largo máximo de un mensaje de chat. */
export const CHAT_MAX_LENGTH = 140;
/** Mensajes que el servidor guarda para quien entra a la sala. */
export const CHAT_HISTORY = 40;

/** `all` = toda la sala; `team` = sólo tu equipo. */
export type ChatScope = 'all' | 'team';

/** Mensaje de chat (cliente → servidor, mensaje `chat`). */
export interface ChatSend {
  text: string;
  scope: ChatScope;
}

/** Mensaje de chat (servidor → clientes, mensaje `chat`). `system` = aviso de la sala (sin autor). */
export interface ChatMessage {
  id: number;
  name: string;
  team: Team;
  scope: ChatScope;
  text: string;
  system?: boolean;
  /** sessionId del autor ('' en los avisos) */
  from: string;
}

/** Texto de chat saneado (vacío si no vale): sin caracteres de control, recortado. */
export function sanitizeChatText(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const clean = Array.from(raw)
    .filter((c) => c >= ' ')
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
  return Array.from(clean).slice(0, CHAT_MAX_LENGTH).join('');
}

/** Emojis de la rueda (T): el índice viaja por la red. Orden = sectores en sentido horario desde arriba. */
export const EMOTES = ['👍', '😂', '😡', '😭', '👋', '🔥', '💀', '🤔'] as const;
/** Como mucho un emoji cada estos ms por jugador. */
export const EMOTE_COOLDOWN_MS = 1200;

/** Emoji sobre un héroe (servidor → clientes, mensaje `emote`; cliente → servidor sólo `{ emote }`). */
export interface EmoteMessage {
  /** id del héroe en la simulación */
  heroId: number;
  /** índice en `EMOTES` */
  emote: number;
}

/** Índice de emoji válido o -1. */
export function sanitizeEmote(raw: unknown): number {
  return typeof raw === 'number' && Number.isInteger(raw) && raw >= 0 && raw < EMOTES.length ? raw : -1;
}

export interface RematchMessage {
  startsAt: number;
}

export interface HeroNetSnapshot {
  id: number;
  heroId: HeroId;
  team: Team;
  name: string;
  isBot: boolean;
  x: number;
  z: number;
  px: number;
  pz: number;
  facing: number;
  aimX: number;
  aimZ: number;
  hp: number;
  maxHp: number;
  radius: number;
  alive: boolean;
  actionKind: ActionKind;
  actionSlot?: AbilitySlot;
  actionTicks: number;
  actionDuration: number;
  actionChain: number;
  actionSerial: number;
  cooldowns: Cooldowns;
  /** cargas (flechas de Link) y ticks hasta la siguiente */
  charges: Cooldowns;
  chargeTicks: Cooldowns;
  hitstun: number;
  invuln: number;
  flashTicks: number;
  kills: number;
  deaths: number;
  damageDealt: number;
  targetId: number;
  gold: number;
  respawnAtTick: number;
  recallTicks: number;
  thornsTicks: number;
  shieldHp: number;
  liftTicks: number;
  liftDuration: number;
  grabbedBy: number;
  level: number;
  xp: number;
  skillPoints: number;
  ranks: Record<AbilitySlot, number>;
  items: string[];
  /**
   * Enemigo fuera de la visión del equipo que recibe el snapshot: sólo viajan su
   * identidad y el marcador (K/D, nivel, objetos); ni posición, ni vida, ni acción.
   */
  hidden?: boolean;
}

/** Campos de un héroe enemigo oculto por la niebla (ver `snapshotForTeam`). */
const HIDDEN_HERO_FIELDS = ['id', 'heroId', 'team', 'name', 'isBot', 'alive', 'maxHp', 'kills', 'deaths', 'damageDealt', 'respawnAtTick', 'level', 'items'] as const;

export interface ProjectileNetSnapshot {
  id: number;
  ownerId: number;
  team: Team;
  kind: ProjectileKind;
  x: number;
  z: number;
  px: number;
  pz: number;
  radius: number;
}

export interface StructureNetSnapshot {
  id: number;
  kind: StructureKind;
  team: Team;
  tier: number;
  lane: number;
  model: HeroId;
  x: number;
  z: number;
  radius: number;
  hp: number;
  maxHp: number;
  alive: boolean;
  targetId: number;
  facing: number;
}

export interface MinionNetSnapshot {
  id: number;
  kind: MinionKind;
  team: Team;
  lane: number;
  x: number;
  z: number;
  px: number;
  pz: number;
  facing: number;
  radius: number;
  hp: number;
  maxHp: number;
  attackTicks: number;
}

export interface WorldSnapshot {
  tick: number;
  serverTime: number;
  heroes: HeroNetSnapshot[];
  structures: StructureNetSnapshot[];
  minions: MinionNetSnapshot[];
  projectiles: ProjectileNetSnapshot[];
  match: MatchState;
  events: SimEvent[];
  /** minions enemigos vivos pero ocultos por la niebla: el cliente los conserva sin mostrarlos */
  fogMinions?: number[];
}

export function snapshotSimulation(sim: Simulation): WorldSnapshot {
  return {
    tick: sim.tick,
    serverTime: Date.now(),
    heroes: sim.heroes.map(heroSnapshot),
    structures: sim.structures.map(structureSnapshot),
    minions: sim.minions.map(minionSnapshot),
    projectiles: sim.projectiles.map(projectileSnapshot),
    match: {
      ...sim.match,
      score: { ...sim.match.score },
    },
    events: [...sim.events],
  };
}

/**
 * Recorta un snapshot a lo que ve `team` (interés por equipo): los héroes
 * enemigos fuera de la visión viajan sin posición ni estado de combate, los
 * minions y proyectiles enemigos ocultos no viajan y los eventos visuales que
 * ocurren en la niebla tampoco. Ahorra ancho de banda y el cliente no puede
 * "quitar la niebla" porque esos datos nunca le llegan.
 */
export function snapshotForTeam(snapshot: WorldSnapshot, sim: Simulation, team: Team): WorldSnapshot {
  if (!sim.match.enabled) return snapshot;
  const sources = visionSources(sim, team);
  const ownIds = new Set<number>();
  for (const hero of sim.heroes) if (hero.team === team) ownIds.add(hero.id);
  for (const minion of sim.minions) if (minion.team === team) ownIds.add(minion.id);
  const seen = (x: number, z: number, radius: number) => isVisible(sources, x, z, radius + VISION_NET_PADDING);

  const heroes = snapshot.heroes.map((hero) => {
    if (hero.team === team || !hero.alive || seen(hero.x, hero.z, hero.radius)) return hero;
    const hidden: Record<string, unknown> = { hidden: true };
    for (const key of HIDDEN_HERO_FIELDS) hidden[key] = hero[key];
    return hidden as unknown as HeroNetSnapshot;
  });
  const minions: MinionNetSnapshot[] = [];
  const fogMinions: number[] = [];
  for (const minion of snapshot.minions) {
    if (minion.team === team || seen(minion.x, minion.z, minion.radius)) minions.push(minion);
    else fogMinions.push(minion.id);
  }
  const projectiles = snapshot.projectiles.filter((projectile) => projectile.team === team || seen(projectile.x, projectile.z, 2));
  const events = snapshot.events.filter((event) => eventVisibleTo(event, team, sources, ownIds));
  return { ...snapshot, heroes, minions, projectiles, events, fogMinions };
}

export function applySnapshot(
  sim: Simulation,
  snapshot: WorldSnapshot,
  localHeroId: number,
): void {
  sim.tick = snapshot.tick;
  sim.match = {
    ...snapshot.match,
    score: { ...snapshot.match.score },
  };

  const byId = new Map(snapshot.heroes.map((hero) => [hero.id, hero]));
  for (const hero of sim.heroes) {
    const state = byId.get(hero.id);
    if (!state) continue;
    applyHeroSnapshot(hero, state, hero.id === localHeroId);
  }

  sim.laneEnabled = (snapshot.structures ?? []).length > 0;
  const structuresById = new Map(sim.structures.map((structure) => [structure.id, structure]));
  sim.structures = (snapshot.structures ?? []).map((state) => {
    const existing = structuresById.get(state.id);
    if (!existing) return structureFromSnapshot(state);
    existing.hp = state.hp;
    existing.maxHp = state.maxHp;
    existing.alive = state.alive;
    existing.targetId = state.targetId;
    existing.facing = state.facing;
    return existing;
  });
  // los minions se crean y mueren constantemente: se reconstruye la lista,
  // conservando objetos existentes para que las vistas no pierdan referencia
  const minionsById = new Map(sim.minions.map((minion) => [minion.id, minion]));
  sim.minions = (snapshot.minions ?? []).map((state) => {
    const minion = minionFromSnapshot(state, minionsById.get(state.id));
    minion.hidden = false;
    return minion;
  });
  // minions enemigos en la niebla: siguen vivos (sin animación de muerte), ocultos y quietos
  for (const id of snapshot.fogMinions ?? []) {
    const minion = minionsById.get(id);
    if (!minion) continue;
    minion.hidden = true;
    minion.prevPos.x = minion.pos.x;
    minion.prevPos.z = minion.pos.z;
    sim.minions.push(minion);
  }
  sim.projectiles = snapshot.projectiles.map(projectileFromSnapshot);
  sim.events = snapshot.events;
}

export function sanitizeInput(raw: unknown): InputState {
  const source = isRecord(raw) ? raw : {};
  const abilities = isRecord(source.abilities) ? source.abilities : {};

  let moveX = finite(source.moveX, 0);
  let moveZ = finite(source.moveZ, 0);
  const moveLength = Math.hypot(moveX, moveZ);
  if (moveLength > 1) {
    moveX /= moveLength;
    moveZ /= moveLength;
  }

  const limit = ARENA_RADIUS + 4;
  const rawMoveTo = isRecord(source.moveTo) ? source.moveTo : undefined;
  const moveTo =
    rawMoveTo && Number.isFinite(rawMoveTo.x) && Number.isFinite(rawMoveTo.z)
      ? { x: clamp(rawMoveTo.x as number, -limit, limit), z: clamp(rawMoveTo.z as number, -limit, limit) }
      : undefined;
  return {
    moveX,
    moveZ,
    ...(moveTo ? { moveTo } : {}),
    aimX: clamp(finite(source.aimX, 0), -limit, limit),
    aimZ: clamp(finite(source.aimZ, 1), -limit, limit),
    attack: source.attack === true,
    recall: source.recall === true,
    levelUp: ['q', 'w', 'e', 'r'].includes(source.levelUp as string) ? (source.levelUp as AbilitySlot) : '',
    buy: typeof source.buy === 'string' && source.buy in ITEMS ? source.buy : '',
    sell: Number.isInteger(source.sell) && (source.sell as number) >= 0 && (source.sell as number) < SHOP.slots ? (source.sell as number) : -1,
    targetId: Math.max(0, Math.floor(finite(source.targetId, 0))),
    abilities: {
      q: abilities.q === true,
      w: abilities.w === true,
      e: abilities.e === true,
      r: abilities.r === true,
    },
  };
}

export function sanitizeInputMessage(raw: unknown): NetInputMessage {
  const source = isRecord(raw) ? raw : {};
  return {
    seq: Math.max(0, Math.floor(finite(source.seq, 0))),
    input: sanitizeInput(source.input),
  };
}

export function emptyNetInput(): InputState {
  return emptyInput();
}

export function mergeInputEdges(base: InputState, pending: InputState): InputState {
  return {
    ...base,
    attack: base.attack || pending.attack,
    recall: base.recall || pending.recall,
    levelUp: pending.levelUp || base.levelUp,
    buy: pending.buy || base.buy,
    sell: pending.sell >= 0 ? pending.sell : base.sell,
    abilities: {
      q: base.abilities.q || pending.abilities.q,
      w: base.abilities.w || pending.abilities.w,
      e: base.abilities.e || pending.abilities.e,
      r: base.abilities.r || pending.abilities.r,
    },
  };
}

function heroSnapshot(hero: HeroState): HeroNetSnapshot {
  return {
    id: hero.id,
    heroId: hero.heroId,
    team: hero.team,
    name: hero.name,
    isBot: hero.isBot,
    x: q(hero.pos.x),
    z: q(hero.pos.z),
    px: q(hero.prevPos.x),
    pz: q(hero.prevPos.z),
    facing: q(hero.facing),
    aimX: q(hero.aim.x),
    aimZ: q(hero.aim.z),
    hp: q(hero.hp),
    maxHp: hero.maxHp,
    radius: hero.radius,
    alive: hero.alive,
    actionKind: hero.action.kind,
    actionSlot: hero.action.slot,
    actionTicks: hero.action.ticks,
    actionDuration: hero.action.duration,
    actionChain: hero.action.chain,
    actionSerial: hero.actionSerial,
    cooldowns: { ...hero.cooldowns },
    charges: { ...hero.charges },
    chargeTicks: { ...hero.chargeTicks },
    hitstun: hero.hitstun,
    invuln: hero.invuln,
    flashTicks: hero.flashTicks,
    kills: hero.kills,
    deaths: hero.deaths,
    damageDealt: hero.damageDealt,
    targetId: hero.targetId,
    gold: hero.gold,
    respawnAtTick: hero.respawnAtTick,
    recallTicks: hero.recallTicks,
    thornsTicks: hero.thornsTicks,
    shieldHp: hero.shieldHp,
    liftTicks: hero.liftTicks,
    liftDuration: hero.liftDuration,
    grabbedBy: hero.grabbedBy,
    level: hero.level,
    xp: q(hero.xp),
    skillPoints: hero.skillPoints,
    ranks: { ...hero.ranks },
    items: [...hero.items],
  };
}

function structureSnapshot(structure: StructureState): StructureNetSnapshot {
  return {
    id: structure.id,
    kind: structure.kind,
    team: structure.team,
    tier: structure.tier,
    lane: structure.lane,
    model: structure.model,
    x: structure.pos.x,
    z: structure.pos.z,
    radius: structure.radius,
    hp: q(structure.hp),
    maxHp: structure.maxHp,
    alive: structure.alive,
    targetId: structure.targetId,
    facing: q(structure.facing),
  };
}

function minionSnapshot(minion: MinionState): MinionNetSnapshot {
  return {
    id: minion.id,
    kind: minion.kind,
    team: minion.team,
    lane: minion.lane,
    x: q(minion.pos.x),
    z: q(minion.pos.z),
    px: q(minion.prevPos.x),
    pz: q(minion.prevPos.z),
    facing: q(minion.facing),
    radius: minion.radius,
    hp: minion.hp,
    maxHp: minion.maxHp,
    attackTicks: minion.attackTicks,
  };
}

function structureFromSnapshot(state: StructureNetSnapshot): StructureState {
  return {
    id: state.id,
    kind: state.kind,
    team: state.team,
    tier: state.tier,
    lane: state.lane,
    model: state.model,
    pos: { x: state.x, z: state.z },
    radius: state.radius,
    hp: state.hp,
    maxHp: state.maxHp,
    alive: state.alive,
    cooldown: 0,
    targetId: state.targetId,
    facing: state.facing,
  };
}

function minionFromSnapshot(state: MinionNetSnapshot, existing?: MinionState): MinionState {
  const minion: MinionState = existing ?? {
    id: state.id,
    kind: state.kind,
    team: state.team,
    lane: state.lane,
    waypoint: 0,
    pos: { x: state.x, z: state.z },
    prevPos: { x: state.px, z: state.pz },
    vel: { x: 0, z: 0 },
    facing: state.facing,
    radius: state.radius,
    speed: 0,
    hp: state.hp,
    maxHp: state.maxHp,
    alive: true,
    cooldown: 0,
    targetId: 0,
    attackTicks: state.attackTicks,
  };
  minion.pos.x = state.x;
  minion.pos.z = state.z;
  minion.prevPos.x = state.px;
  minion.prevPos.z = state.pz;
  minion.facing = state.facing;
  minion.hp = state.hp;
  minion.maxHp = state.maxHp;
  minion.attackTicks = state.attackTicks;
  minion.alive = true;
  return minion;
}

function projectileSnapshot(projectile: ProjectileState): ProjectileNetSnapshot {
  return {
    id: projectile.id,
    ownerId: projectile.ownerId,
    team: projectile.team,
    kind: projectile.kind,
    x: q(projectile.pos.x),
    z: q(projectile.pos.z),
    px: q(projectile.prevPos.x),
    pz: q(projectile.prevPos.z),
    radius: projectile.radius,
  };
}

function applyHeroSnapshot(hero: HeroState, state: HeroNetSnapshot, isLocal: boolean): void {
  hero.heroId = state.heroId;
  hero.team = state.team;
  hero.name = state.name;
  hero.isPlayer = isLocal;
  hero.isBot = state.isBot;
  hero.isDummy = false;
  hero.hidden = Boolean(state.hidden);
  if (state.hidden) {
    // enemigo en la niebla: sólo el marcador; se queda quieto donde se le vio
    hero.prevPos.x = hero.pos.x;
    hero.prevPos.z = hero.pos.z;
    hero.alive = state.alive;
    hero.maxHp = state.maxHp ?? hero.maxHp;
    hero.kills = state.kills ?? hero.kills;
    hero.deaths = state.deaths ?? hero.deaths;
    hero.damageDealt = state.damageDealt ?? hero.damageDealt;
    hero.respawnAtTick = state.respawnAtTick ?? hero.respawnAtTick;
    hero.level = state.level ?? hero.level;
    hero.items = state.items ? [...state.items] : hero.items;
    hero.targetId = 0;
    return;
  }
  hero.pos.x = state.x;
  hero.pos.z = state.z;
  hero.prevPos.x = state.px;
  hero.prevPos.z = state.pz;
  hero.vel.x = 0;
  hero.vel.z = 0;
  hero.facing = state.facing;
  hero.aim.x = state.aimX;
  hero.aim.z = state.aimZ;
  hero.hp = state.hp;
  hero.maxHp = state.maxHp;
  hero.radius = state.radius;
  hero.alive = state.alive;
  hero.action = {
    kind: state.actionKind,
    slot: state.actionSlot,
    ticks: state.actionTicks,
    duration: state.actionDuration,
    chain: state.actionChain,
    hitIds: [],
  };
  hero.actionSerial = state.actionSerial;
  hero.cooldowns = { ...state.cooldowns };
  if (state.charges) hero.charges = { ...state.charges };
  if (state.chargeTicks) hero.chargeTicks = { ...state.chargeTicks };
  hero.hitstun = state.hitstun;
  hero.invuln = state.invuln;
  hero.chainWindow = 0;
  hero.attackCooldown = 0;
  hero.comboChain = 0;
  hero.lastEnemyHeroId = 0;
  hero.lastEnemyHeroAt = 0;
  hero.assistContributors = [];
  hero.flashTicks = state.flashTicks;
  hero.kills = state.kills;
  hero.deaths = state.deaths;
  hero.damageDealt = state.damageDealt;
  hero.targetId = state.targetId ?? 0;
  hero.gold = state.gold ?? 0;
  hero.respawnAtTick = state.respawnAtTick ?? 0;
  hero.recallTicks = state.recallTicks ?? 0;
  hero.thornsTicks = state.thornsTicks ?? 0;
  hero.shieldHp = state.shieldHp ?? 0;
  hero.liftTicks = state.liftTicks ?? 0;
  hero.liftDuration = state.liftDuration ?? 0;
  hero.grabbedBy = state.grabbedBy ?? 0;
  hero.level = state.level ?? 1;
  hero.xp = state.xp ?? 0;
  hero.skillPoints = state.skillPoints ?? 0;
  hero.ranks = state.ranks ? { ...state.ranks } : { q: 1, w: 1, e: 1, r: 1 };
  hero.items = state.items ? [...state.items] : [];
}

function projectileFromSnapshot(state: ProjectileNetSnapshot): ProjectileState {
  return {
    id: state.id,
    ownerId: state.ownerId,
    team: state.team,
    kind: state.kind,
    pos: { x: state.x, z: state.z },
    prevPos: { x: state.px, z: state.pz },
    // el snapshot no lleva la velocidad: se deduce del último tick (orienta flechas y bombas)
    vel: { x: (state.x - state.px) * TICK_RATE, z: (state.z - state.pz) * TICK_RATE },
    radius: state.radius,
    damage: 0,
    knockback: 0,
    hitstun: 0,
    ttl: 1,
    alive: true,
    reflected: false,
    pierce: 0,
    hitIds: [],
    explodeRadius: 0,
    explodeDamage: 0,
    explodeKnockback: 0,
    explodeHitstun: 0,
    explodeOnExpire: false,
    explodeOnHit: false,
    homingId: 0,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function finite(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

// ------------------------------------------------------- snapshots compactos

/** Lista de objetos como tabla: los nombres de campo una vez y las filas como arrays. */
export interface NetTable {
  k: string[];
  v: unknown[][];
}

/**
 * Snapshot tal como viaja por la red (`snapshot`). Repetir los nombres de campo
 * de cada héroe, minion y torre 30 veces por segundo pesaba ~9 KB por snapshot
 * (~280 KB/s por jugador) y la conexión los entregaba a ráfagas. Como tabla, y
 * con sólo las estructuras que cambiaron, ronda un tercio.
 */
export interface PackedSnapshot {
  /** versión del formato compacto */
  f: 1;
  tick: number;
  serverTime: number;
  heroes: NetTable;
  /** estructuras que cambiaron desde el anterior (todas si `full`) */
  structures: NetTable;
  /** ids de todas las estructuras en orden (para reconstruir la lista) */
  structureIds: number[];
  full: boolean;
  minions: NetTable;
  projectiles: NetTable;
  match: MatchState;
  events: SimEvent[];
  fogMinions?: number[];
}

export function toTable(rows: readonly object[]): NetTable {
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (seen.has(key)) continue;
      seen.add(key);
      keys.push(key);
    }
  }
  return { k: keys, v: rows.map((row) => keys.map((key) => (row as Record<string, unknown>)[key])) };
}

export function fromTable<T>(table: NetTable): T[] {
  return table.v.map((values) => {
    const row: Record<string, unknown> = {};
    table.k.forEach((key, i) => {
      const value = values[i];
      // los campos opcionales ausentes viajan como undefined/null: se omiten
      if (value !== undefined && value !== null) row[key] = value;
    });
    return row as T;
  });
}

/**
 * Codifica los snapshots de una partida (servidor, uno por sala): las
 * estructuras sólo viajan cuando cambian. WebSocket es fiable y ordenado, así
 * que el `SnapshotDecoder` del cliente puede reconstruir el estado completo.
 */
export class SnapshotEncoder {
  private readonly lastStructures = new Map<number, string>();

  /** `full`: todas las estructuras (al entrar) y no altera lo ya emitido. */
  encode(snapshot: WorldSnapshot, full = false): PackedSnapshot {
    const changed: StructureNetSnapshot[] = [];
    for (const structure of snapshot.structures) {
      const key = JSON.stringify(structure);
      if (full) {
        changed.push(structure);
        continue;
      }
      if (this.lastStructures.get(structure.id) === key) continue;
      this.lastStructures.set(structure.id, key);
      changed.push(structure);
    }
    return {
      f: 1,
      tick: snapshot.tick,
      serverTime: snapshot.serverTime,
      heroes: toTable(snapshot.heroes),
      structures: toTable(changed),
      structureIds: snapshot.structures.map((structure) => structure.id),
      full,
      minions: toTable(snapshot.minions),
      projectiles: toTable(snapshot.projectiles),
      match: snapshot.match,
      events: snapshot.events,
      ...(snapshot.fogMinions?.length ? { fogMinions: snapshot.fogMinions } : {}),
    };
  }

  reset(): void {
    this.lastStructures.clear();
  }
}

/** Reconstruye en el cliente los `WorldSnapshot` completos a partir de los compactos. */
export class SnapshotDecoder {
  private readonly structures = new Map<number, StructureNetSnapshot>();

  decode(packed: PackedSnapshot | WorldSnapshot): WorldSnapshot {
    // compatibilidad: un snapshot sin compactar pasa tal cual
    if (!('f' in packed)) return packed;
    if (packed.full) this.structures.clear();
    for (const structure of fromTable<StructureNetSnapshot>(packed.structures)) this.structures.set(structure.id, structure);
    const structures: StructureNetSnapshot[] = [];
    for (const id of packed.structureIds) {
      const structure = this.structures.get(id);
      if (structure) structures.push(structure);
    }
    return {
      tick: packed.tick,
      serverTime: packed.serverTime,
      heroes: fromTable<HeroNetSnapshot>(packed.heroes),
      structures,
      minions: fromTable<MinionNetSnapshot>(packed.minions),
      projectiles: fromTable<ProjectileNetSnapshot>(packed.projectiles),
      match: packed.match,
      events: packed.events,
      ...(packed.fogMinions ? { fogMinions: packed.fogMinions } : {}),
    };
  }

  reset(): void {
    this.structures.clear();
  }
}
